import {
  createContext,
  FormEvent,
  KeyboardEvent as ReactKeyboardEvent,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createHttpClient } from "@shared/api/httpClient";
import { getRuntimeConfig } from "@shared/config/runtime";
import type {
  AiChatRequest,
  AiChatResponse,
  AiSourceReference,
  AiToolResult,
} from "@shared/types/api";

type AiAssistantScope = {
  clientId?: string;
  accountId?: string;
  clientName?: string;
  packageId?: string;
  packageName?: string;
  packageType?: string;
  controlId?: string;
  controlTitle?: string;
  documentId?: string;
  pageType?: "CLIENT" | "PACKAGE_DETAIL" | "CONTROL_DETAIL" | "DOCUMENT_DETAIL" | string;
  sourceLabel?: string;
};

type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  sourceReferences?: AiSourceReference[];
  toolResults?: AiToolResult[];
};

type AiAssistantContextValue = {
  scope: AiAssistantScope;
  setPageScope: (scope: AiAssistantScope) => void;
  clearPageScope: () => void;
  openDrawer: (scope?: AiAssistantScope, draft?: string, autoSend?: boolean) => void;
  closeDrawer: () => void;
};

const AiAssistantContext = createContext<AiAssistantContextValue | null>(null);

function scopeSignature(scope: AiAssistantScope): string {
  return [
    scope.clientId,
    scope.accountId,
    scope.packageId,
    scope.packageType,
    scope.controlId,
    scope.documentId,
    scope.pageType,
  ]
    .map((value) => String(value ?? "").trim())
    .join("|");
}

function mergeScopes(
  base: AiAssistantScope,
  overlay?: AiAssistantScope,
): AiAssistantScope {
  const merged: AiAssistantScope = { ...base };
  for (const [key, value] of Object.entries(overlay ?? {}) as Array<[
    keyof AiAssistantScope,
    string | undefined,
  ]>) {
    if (value === undefined || value === null) continue;
    const normalized = String(value).trim();
    if (!normalized) {
      delete merged[key];
      continue;
    }
    merged[key] = normalized as never;
  }
  return merged;
}

function normalizeRequestedScope(scope?: AiAssistantScope): AiAssistantScope | undefined {
  if (!scope) return undefined;
  const normalized: AiAssistantScope = { ...scope };

  // Opening the drawer from a package-level button must clear stale control/document context.
  if (normalized.packageId && !normalized.controlId && !normalized.documentId) {
    normalized.controlId = "";
    normalized.controlTitle = "";
    normalized.documentId = "";
    normalized.pageType = normalized.pageType || "PACKAGE_DETAIL";
  }

  // Opening from a control-level button must not retain a previously selected document.
  if (normalized.controlId && !normalized.documentId) {
    normalized.documentId = "";
    normalized.pageType = normalized.pageType || "CONTROL_DETAIL";
  }

  if (normalized.documentId) {
    normalized.pageType = normalized.pageType || "DOCUMENT_DETAIL";
  }

  return normalized;
}

function scopeTitle(scope: AiAssistantScope): string {
  if (scope.controlId)
    return scope.controlTitle
      ? `${scope.controlId} · ${scope.controlTitle}`
      : scope.controlId;
  if (scope.documentId) return compactIdentifier(scope.documentId, 48);
  if (scope.packageId)
    return compactIdentifier(scope.packageName || scope.packageId, 56);
  if (scope.clientName) return scope.clientName;
  if (scope.clientId) return scope.clientId;
  return "No client selected";
}

function compactIdentifier(value?: string, maxLength = 34): string {
  const text = String(value || "").trim();
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(8, maxLength - 12))}…${text.slice(-8)}`;
}

function friendlyScopeValue(value?: string): string {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function packageScopeLabel(scope: AiAssistantScope): string {
  const typeLabel = friendlyScopeValue(scope.packageType);
  if (typeLabel) return typeLabel;
  if (scope.packageName && scope.packageName !== scope.packageId)
    return compactIdentifier(scope.packageName, 42);
  if (scope.packageId) return "Selected package";
  return "";
}

function firstErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return "The AI Assistant request failed.";
}

function sourceLabel(source: AiSourceReference): string {
  const label =
    source.label || source.description || source.sourceType || "Source";
  const detail =
    source.relativePath ||
    source.metadata?.key ||
    source.key ||
    source.recordKey ||
    source.sourceId ||
    source.location ||
    source.s3Uri ||
    "";
  return detail ? `${label}: ${detail}` : label;
}

function toolStatusClass(tool: AiToolResult): string {
  if (!tool.ok) return "ai-tool-status ai-tool-status-error";
  const status = String(tool.status || tool.toolStatus || "").toUpperCase();
  if (status.includes("PARTIAL") || status.includes("PENDING"))
    return "ai-tool-status ai-tool-status-warning";
  return "ai-tool-status ai-tool-status-ok";
}

function cleanAssistantAnswer(content: string): string {
  const normalized = String(content || "")
    .replace(/\r\n/g, "\n")
    .trim();
  if (!normalized) return "";

  const lines = normalized.split("\n");
  const artifactHeadings = new Set([
    "source references",
    "sources",
    "metadata",
    "session metadata",
  ]);
  let cutoff = lines.length;

  for (let index = 0; index < lines.length; index += 1) {
    const cleanedHeading = lines[index]
      .trim()
      .replace(/^[-*_\s]+$/, "")
      .replace(/^#{1,6}\s*/, "")
      .replace(/^\*\*/, "")
      .replace(/\*\*$/, "")
      .replace(/:$/, "")
      .trim()
      .toLowerCase();

    if (artifactHeadings.has(cleanedHeading)) {
      cutoff = index;
      break;
    }
  }

  const answerLines = lines.slice(0, cutoff);
  while (
    answerLines.length &&
    /^\s*(?:---|#{1,6})?\s*$/.test(answerLines[answerLines.length - 1])
  ) {
    answerLines.pop();
  }

  return answerLines.join("\n").trim();
}

function renderInlineMarkdown(text: string, keyPrefix: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const token = match[0];
    const key = `${keyPrefix}-${match.index}`;
    if (token.startsWith("**")) {
      parts.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("`")) {
      parts.push(<code key={key}>{token.slice(1, -1)}</code>);
    }
    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

function stripListMarker(line: string): string {
  return line.replace(/^\s*(?:[-*•]|\d+\.)\s+/, "").trim();
}

type OrderedListItem = {
  ordinal: number;
  text: string;
};

function parseOrderedListItem(line: string): OrderedListItem | null {
  const match = /^\s*(\d+)\.\s+(.+)$/.exec(line);
  if (!match) return null;

  const ordinal = Number.parseInt(match[1], 10);
  if (!Number.isFinite(ordinal) || ordinal < 1) return null;

  return { ordinal, text: match[2].trim() };
}

function formatAssistantAnswer(content: string): ReactNode[] {
  const cleaned = cleanAssistantAnswer(content);
  if (!cleaned) return [<p key="empty">No answer was returned.</p>];

  const lines = cleaned.split("\n");
  const blocks: ReactNode[] = [];
  let index = 0;
  let paragraph: string[] = [];

  function flushParagraph() {
    const text = paragraph.join(" ").replace(/\s+/g, " ").trim();
    if (text) {
      blocks.push(
        <p key={`p-${blocks.length}`}>
          {renderInlineMarkdown(text, `p-${blocks.length}`)}
        </p>,
      );
    }
    paragraph = [];
  }

  while (index < lines.length) {
    const rawLine = lines[index];
    const line = rawLine.trim();

    if (!line) {
      flushParagraph();
      index += 1;
      continue;
    }

    if (/^---+$/.test(line)) {
      flushParagraph();
      blocks.push(<hr key={`hr-${blocks.length}`} />);
      index += 1;
      continue;
    }

    const heading = /^(#{1,6})\s+(.+)$/.exec(line);
    if (heading) {
      flushParagraph();
      const headingText = heading[2].trim();
      const headingKey = `h-${blocks.length}`;
      if (heading[1].length <= 2) {
        blocks.push(
          <h3 key={headingKey}>
            {renderInlineMarkdown(headingText, headingKey)}
          </h3>,
        );
      } else {
        blocks.push(
          <h4 key={headingKey}>
            {renderInlineMarkdown(headingText, headingKey)}
          </h4>,
        );
      }
      index += 1;
      continue;
    }

    if (/^\s*[-*•]\s+/.test(rawLine)) {
      flushParagraph();
      const items: string[] = [];
      while (index < lines.length) {
        if (/^\s*[-*•]\s+/.test(lines[index])) {
          items.push(stripListMarker(lines[index]));
          index += 1;
          continue;
        }

        if (
          !lines[index].trim() &&
          index + 1 < lines.length &&
          /^\s*[-*•]\s+/.test(lines[index + 1])
        ) {
          index += 1;
          continue;
        }

        break;
      }
      const listKey = `ul-${blocks.length}`;
      blocks.push(
        <ul key={listKey}>
          {items.map((item, itemIndex) => (
            <li key={`${listKey}-${itemIndex}`}>
              {renderInlineMarkdown(item, `${listKey}-${itemIndex}`)}
            </li>
          ))}
        </ul>,
      );
      continue;
    }

    if (/^\s*\d+\.\s+/.test(rawLine)) {
      flushParagraph();
      const items: OrderedListItem[] = [];
      while (index < lines.length) {
        const item = parseOrderedListItem(lines[index]);
        if (item) {
          items.push(item);
          index += 1;
          continue;
        }

        if (
          !lines[index].trim() &&
          index + 1 < lines.length &&
          parseOrderedListItem(lines[index + 1])
        ) {
          index += 1;
          continue;
        }

        break;
      }
      const listKey = `ol-${blocks.length}`;
      blocks.push(
        <ol key={listKey} start={items[0]?.ordinal}>
          {items.map((item, itemIndex) => (
            <li key={`${listKey}-${itemIndex}`} value={item.ordinal}>
              {renderInlineMarkdown(item.text, `${listKey}-${itemIndex}`)}
            </li>
          ))}
        </ol>,
      );
      continue;
    }

    paragraph.push(line);
    index += 1;
  }

  flushParagraph();
  return blocks;
}

function FormattedAiMessage({ content }: { content: string }) {
  return (
    <div className="ai-formatted-answer">{formatAssistantAnswer(content)}</div>
  );
}

const PACKAGE_PROMPTS = [
  "Summarize this package for an administrator.",
  "Which findings require client follow-up?",
  "What changed from the previous comparable package?",
  "What should I verify before assessor handoff?",
];

const CONTROL_PROMPTS = [
  "Explain this control status for administrator review.",
  "Which trusted evidence supports this result?",
  "What client follow-up is still required?",
  "What should I verify before assessor review?",
];

const ADMIN_WORKSPACE_PROMPTS = [
  "Explain CMMC assessor-preparation responsibilities for an administrator.",
  "How should I review evidence quality and maturity?",
  "Help me plan an AWS or cloud security review.",
  "Explain a security control or compliance requirement.",
];

function promptChips(scope: AiAssistantScope): string[] {
  if (scope.controlId) return CONTROL_PROMPTS;
  if (scope.packageId) return PACKAGE_PROMPTS;
  return ADMIN_WORKSPACE_PROMPTS;
}

export function AiAssistantProvider({ children }: PropsWithChildren) {
  const [pageScope, setPageScopeState] = useState<AiAssistantScope>({});
  const [manualScope, setManualScope] = useState<AiAssistantScope>({});
  const [isOpen, setIsOpen] = useState(false);
  const [pendingDraft, setPendingDraft] = useState("");
  const [pendingAutoSend, setPendingAutoSend] = useState(false);

  const activeScope = useMemo(
    () => mergeScopes(manualScope, pageScope.clientId ? pageScope : undefined),
    [manualScope, pageScope],
  );

  const setPageScope = useCallback((scope: AiAssistantScope) => {
    setPageScopeState(normalizeRequestedScope(scope) ?? {});
  }, []);

  const clearPageScope = useCallback(() => {
    setPageScopeState({});
    setManualScope((current) =>
      current.packageId || current.controlId || current.documentId ? {} : current,
    );
  }, []);

  const openDrawer = useCallback(
    (scope?: AiAssistantScope, draft?: string, autoSend = Boolean(draft)) => {
      const normalizedScope = normalizeRequestedScope(scope);
      if (normalizedScope) {
        setManualScope((current) => mergeScopes(current, normalizedScope));
        setPageScopeState((current) => mergeScopes(current, normalizedScope));
      }
      if (draft) {
        setPendingDraft(draft);
        setPendingAutoSend(autoSend);
      }
      setIsOpen(true);
    },
    [],
  );

  const closeDrawer = useCallback(() => setIsOpen(false), []);

  return (
    <AiAssistantContext.Provider
      value={{
        scope: activeScope,
        setPageScope,
        clearPageScope,
        openDrawer,
        closeDrawer,
      }}
    >
      {children}
      <AiAssistantDrawer
        scope={activeScope}
        isOpen={isOpen}
        pendingDraft={pendingDraft}
        pendingAutoSend={pendingAutoSend}
        clearPendingDraft={() => {
          setPendingDraft("");
          setPendingAutoSend(false);
        }}
        openDrawer={openDrawer}
        closeDrawer={closeDrawer}
      />
    </AiAssistantContext.Provider>
  );
}

export function useAiAssistant() {
  const value = useContext(AiAssistantContext);
  if (!value) {
    throw new Error("useAiAssistant must be used within AiAssistantProvider.");
  }
  return value;
}

export function useAiAssistantPageScope(scope: AiAssistantScope) {
  const { setPageScope, clearPageScope } = useAiAssistant();
  const signature = scopeSignature(scope);

  useEffect(() => {
    setPageScope(scope);
    return () => clearPageScope();
    // signature intentionally controls when equivalent scope values reset the page context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, setPageScope, clearPageScope]);
}

function AiAssistantDrawer({
  scope,
  isOpen,
  pendingDraft,
  pendingAutoSend,
  clearPendingDraft,
  openDrawer,
  closeDrawer,
}: {
  scope: AiAssistantScope;
  isOpen: boolean;
  pendingDraft: string;
  pendingAutoSend: boolean;
  clearPendingDraft: () => void;
  openDrawer: (scope?: AiAssistantScope, draft?: string, autoSend?: boolean) => void;
  closeDrawer: () => void;
}) {
  const config = getRuntimeConfig();
  const enabled = config.aiAssistantAdminPortalEnabled !== false;
  const api = useMemo(() => createHttpClient({ portal: "admin" }), []);
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastScopeSignature = useRef("");
  const sessionScopeSignatureRef = useRef("");
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const scrollTargetMessageRef = useRef<HTMLElement | null>(null);
  const [scrollTargetMessageId, setScrollTargetMessageId] = useState<string | undefined>();

  const pageContextLocked = Boolean(scope.clientId || scope.packageId || scope.controlId || scope.documentId);
  const activeSignature = scopeSignature(scope);
  const headerTitle = pageContextLocked ? scopeTitle(scope) : "Administrator workspace";

  useEffect(() => {
    if (!pendingDraft) return;
    const nextDraft = pendingDraft;
    const shouldAutoSend = pendingAutoSend;
    setDraft(nextDraft);
    clearPendingDraft();
    if (shouldAutoSend) {
      window.setTimeout(() => {
        void sendMessageText(nextDraft);
      }, 0);
    }
    // sendMessageText intentionally reads the latest drawer scope after openDrawer updates it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDraft, pendingAutoSend, clearPendingDraft]);

  useEffect(() => {
    if (
      lastScopeSignature.current &&
      lastScopeSignature.current !== activeSignature
    ) {
      setSessionId(undefined);
      sessionScopeSignatureRef.current = activeSignature;
      setMessages([]);
      setScrollTargetMessageId(undefined);
      setError(null);
    }
    if (!sessionScopeSignatureRef.current) {
      sessionScopeSignatureRef.current = activeSignature;
    }
    lastScopeSignature.current = activeSignature;
  }, [activeSignature]);

  useEffect(() => {
    if (!scrollTargetMessageId) return;
    const frameId = window.requestAnimationFrame(() => {
      scrollTargetMessageRef.current?.scrollIntoView({
        block: "start",
        behavior: "smooth",
      });
      setScrollTargetMessageId(undefined);
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [messages, scrollTargetMessageId]);

  const canSend = enabled && draft.trim().length > 0 && !loading;
  const composerPlaceholder = pageContextLocked
    ? "Ask about this package, control, evidence, or assessor preparation…"
    : "Ask an admin, SURELINC platform, CMMC, AWS, cloud security, or compliance question…";

  function focusComposer() {
    window.setTimeout(() => textareaRef.current?.focus(), 0);
  }

  function usePrompt(prompt: string) {
    if (loading) {
      setDraft(prompt);
      focusComposer();
      return;
    }
    void sendMessageText(prompt);
  }

  function buildChatRequest(message: string): AiChatRequest & {
    pageContext?: Record<string, string | undefined>;
    selectedPackageId?: string;
    selectedControlId?: string;
    selectedDocumentId?: string;
    sourceLabel?: string;
  } {
    const request: AiChatRequest & {
      pageContext?: Record<string, string | undefined>;
      selectedPackageId?: string;
      selectedControlId?: string;
      selectedDocumentId?: string;
      sourceLabel?: string;
    } = {
      message,
      sessionId:
        sessionScopeSignatureRef.current === activeSignature
          ? sessionId
          : undefined,
      clientId: scope.clientId,
      accountId: scope.accountId,
      packageId: scope.packageId,
      packageType: scope.packageType,
      controlId: scope.controlId,
      documentId: scope.documentId,
    };

    if (scope.packageId) {
      request.selectedPackageId = scope.packageId;
      request.pageContext = {
        pageType: scope.pageType || "PACKAGE_DETAIL",
        clientId: scope.clientId,
        accountId: scope.accountId,
        packageId: scope.packageId,
        packageType: scope.packageType,
        controlId: scope.controlId,
        documentId: scope.documentId,
      };
    }
    if (scope.controlId) request.selectedControlId = scope.controlId;
    if (scope.documentId) request.selectedDocumentId = scope.documentId;
    if (scope.sourceLabel) request.sourceLabel = scope.sourceLabel;

    return request;
  }

  async function sendMessageText(rawMessage: string) {
    const message = rawMessage.trim();
    if (!enabled || !message || loading) return;

    const request = buildChatRequest(message);
    const userMessage: ChatMessage = {
      id: `local-${Date.now()}`,
      role: "user",
      content: message,
    };

    setMessages((current) => [...current, userMessage]);
    setScrollTargetMessageId(userMessage.id);
    setDraft("");
    setLoading(true);
    setError(null);

    try {
      const response = await api.post<AiChatResponse>("/ai/chat", request);
      setSessionId(response.sessionId);
      sessionScopeSignatureRef.current = activeSignature;
      setMessages((current) => [
        ...current,
        {
          id: response.messageId || `assistant-${Date.now()}`,
          role: "assistant",
          content: response.answer || "No answer was returned.",
          sourceReferences: response.sourceReferences ?? [],
          toolResults: response.toolResults ?? [],
        },
      ]);
    } catch (requestError) {
      setError(firstErrorMessage(requestError));
    } finally {
      setLoading(false);
      focusComposer();
    }
  }

  async function sendMessage(event?: FormEvent) {
    event?.preventDefault();
    await sendMessageText(draft);
  }

  function handleComposerKeyDown(
    event: ReactKeyboardEvent<HTMLTextAreaElement>,
  ) {
    if (
      event.key !== "Enter" ||
      event.shiftKey ||
      event.nativeEvent.isComposing
    )
      return;
    event.preventDefault();
    if (canSend) void sendMessage();
  }

  function startNewChat() {
    setSessionId(undefined);
    sessionScopeSignatureRef.current = activeSignature;
    setMessages([]);
    setScrollTargetMessageId(undefined);
    setDraft("");
    setError(null);
  }



  return (
    <>
      <button
        className="ai-assistant-launcher"
        type="button"
        onClick={() => openDrawer()}
        aria-label="Open SURELINC Readiness AI Assistant™"
        title="Open SURELINC Readiness AI Assistant™"
      >
        <span aria-hidden="true">✦</span>
        <span>SURELINC AI</span>
      </button>

      {isOpen && (
        <div
          className="ai-assistant-backdrop"
          onClick={closeDrawer}
          aria-hidden="true"
        />
      )}

      <aside
        className={
          isOpen
            ? "ai-assistant-drawer ai-assistant-drawer-open"
            : "ai-assistant-drawer"
        }
        aria-label="SURELINC Readiness AI Assistant™"
        aria-hidden={!isOpen}
      >
        <header className="ai-assistant-header">
          <div className="ai-assistant-header-copy">
            <div className="ai-assistant-kicker">
              SURELINC Readiness AI Assistant™
            </div>
            <h2>{headerTitle}</h2>
          </div>
          <div className="ai-assistant-header-actions">
            <button
              className="secondary-button"
              type="button"
              onClick={startNewChat}
            >
              New
            </button>
            <button
              className="ai-icon-button"
              type="button"
              onClick={closeDrawer}
              aria-label="Close SURELINC Readiness AI Assistant"
            >
              ×
            </button>
          </div>
        </header>

        {!enabled && (
          <div className="ai-assistant-notice ai-assistant-notice-warning">
            AI Assistant is not enabled for this Admin Portal deployment.
          </div>
        )}

        {enabled && (
          <>
            <section className="ai-admin-scope-bar" aria-label="AI Assistant scope">
              <div className="ai-admin-scope-summary">
                <div>
                  <span>Administrator scope</span>
                  <strong>
                    {pageContextLocked
                      ? `${scope.clientName || scope.clientId || "Selected client"}${scope.accountId ? ` · AWS ${scope.accountId}` : ""}`
                      : "General admin and platform guidance"}
                  </strong>
                </div>
              </div>
            </section>

            {(scope.packageId || scope.controlId) && (
              <section className="ai-assistant-scope-card">
                {scope.packageId && (
                  <div>
                    <span>Package</span>
                    <strong title={scope.packageId}>
                      {packageScopeLabel(scope) ||
                        compactIdentifier(scope.packageName || scope.packageId, 42)}
                    </strong>
                  </div>
                )}
                {scope.controlId && (
                  <div>
                    <span>Control</span>
                    <strong>
                      {scope.controlTitle
                        ? `${scope.controlId} · ${scope.controlTitle}`
                        : scope.controlId}
                    </strong>
                  </div>
                )}
              </section>
            )}

            <section
              className="ai-assistant-prompts"
              aria-label="Suggested administrator prompts"
            >
              {promptChips(scope).map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => usePrompt(prompt)}
                  disabled={loading}
                >
                  {prompt}
                </button>
              ))}
            </section>

            <section className="ai-assistant-messages" aria-live="polite">
              {messages.length === 0 && (
                <div className="ai-assistant-empty-state">
                  <h3>Administrator assistance</h3>
                  <p>
                    Review package and control evidence, prepare for assessor activity, or get guidance on
                    SURELINC, CMMC, AWS, cloud security, and compliance administration.
                  </p>
                </div>
              )}

              {messages.map((message) => (
                <article
                  key={message.id}
                  ref={
                    message.id === scrollTargetMessageId
                      ? scrollTargetMessageRef
                      : undefined
                  }
                  className={`ai-message ai-message-${message.role}`}
                >
                  <div className="ai-message-role">
                    {message.role === "user"
                      ? "You"
                      : "SURELINC Readiness AI Assistant™"}
                  </div>
                  <div className="ai-message-content">
                    {message.role === "assistant" ? (
                      <FormattedAiMessage content={message.content} />
                    ) : (
                      message.content
                    )}
                  </div>
                  {message.role === "assistant" && (
                    <AiAssistantDiagnostics
                      sources={message.sourceReferences ?? []}
                      tools={message.toolResults ?? []}
                    />
                  )}
                </article>
              ))}
              {loading && (
                <div className="ai-assistant-loading">
                  Reviewing trusted administrator and readiness context…
                </div>
              )}
              {error && (
                <div className="ai-assistant-notice ai-assistant-notice-error">
                  {error}
                </div>
              )}
            </section>

            <form className="ai-assistant-composer" onSubmit={sendMessage}>
              <label
                className="ai-assistant-composer-label"
                htmlFor="ai-assistant-direct-question"
              >
                Ask a direct question
              </label>
              <textarea
                id="ai-assistant-direct-question"
                ref={textareaRef}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={handleComposerKeyDown}
                placeholder={composerPlaceholder}
                rows={3}
                maxLength={2000}
                disabled={loading}
                aria-describedby="ai-assistant-composer-help"
              />
              <div className="ai-assistant-composer-footer">
                <span id="ai-assistant-composer-help">
                  Enter to send · Shift+Enter for a new line · {draft.length}/2000
                </span>
                <button
                  className="primary-button"
                  type="submit"
                  disabled={!canSend}
                >
                  {loading ? "Sending…" : "Send"}
                </button>
              </div>
              <p className="ai-assistant-caution">
                AI-generated guidance can make mistakes. Verify important details
                against package records, client records, and assessor requirements.
              </p>
            </form>
          </>
        )}
      </aside>
    </>
  );
}

function AiAssistantDiagnostics({
  sources,
  tools,
}: {
  sources: AiSourceReference[];
  tools: AiToolResult[];
}) {
  if (!sources.length && !tools.length) return null;

  return (
    <div className="ai-diagnostics">
      {sources.length > 0 && (
        <details>
          <summary>Sources ({sources.length})</summary>
          <ol>
            {sources.map((source, index) => (
              <li
                key={`${source.sourceId || source.location || "source"}-${index}`}
              >
                {sourceLabel(source)}
              </li>
            ))}
          </ol>
        </details>
      )}

      {tools.length > 0 && (
        <details>
          <summary>Tool trace ({tools.length})</summary>
          <div className="ai-tool-list">
            {tools.map((tool, index) => (
              <div className="ai-tool-row" key={`${tool.toolName}-${index}`}>
                <span className={toolStatusClass(tool)}>
                  {tool.ok ? "OK" : "CHECK"}
                </span>
                <div>
                  <strong>
                    {tool.clientName
                      ? `${String(tool.clientName)} · ${tool.toolName}`
                      : tool.toolName}
                  </strong>
                  <p>{tool.summary || tool.status || tool.toolStatus}</p>
                </div>
                <span className="ai-tool-source-count">
                  {tool.sourceCount ?? 0} src
                </span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
